import React, { useEffect, useState, useCallback } from 'react';
import {
  Users, Plus, Search, X, School, Eye, EyeOff,
  CheckCircle, XCircle, Mail, Phone, Calendar
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api/client';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const EMPTY_FORM = { full_name: '', email: '', mobile: '', schoolId: '', password: '' };

// ── Create modal ───────────────────────────────────────────────
const CreateModal = ({ schools, onClose, onCreated }) => {
  const [form, setForm]       = useState(EMPTY_FORM);
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving]   = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.full_name || !form.email || !form.schoolId || !form.password)
      return toast.error('Please fill all required fields');
    if (form.password.length < 6)
      return toast.error('Password must be at least 6 characters');

    setSaving(true);
    try {
      await api.post('/school-users', form);
      toast.success('School user created successfully');
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to create user');
    } finally { setSaving(false); }
  };

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, boxShadow: '0 24px 80px rgba(0,0,0,0.15)' }}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #F3EDE8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1C0A00' }}>Create School User</div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>This user will log into the School Portal</div>
          </div>
          <button onClick={onClose} style={{ background: '#FEF3EE', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer', color: '#C84B0F', display: 'flex' }}><X size={16} /></button>
        </div>

        {/* Form */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* School dropdown */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              School <span style={{ color: '#C84B0F' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <School size={14} color="#C84B0F" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <select value={form.schoolId} onChange={e => set('schoolId', e.target.value)}
                style={{ width: '100%', padding: '9px 12px 9px 32px', border: `1px solid ${form.schoolId ? '#F0DCC8' : '#F0DCC8'}`, borderRadius: 9, fontSize: 13, color: form.schoolId ? '#1C0A00' : '#9CA3AF', background: '#FFFCFA', appearance: 'none', outline: 'none', cursor: 'pointer' }}>
                <option value="">Select a school…</option>
                {schools.map(s => <option key={s.id} value={s.id}>{s.school_name}</option>)}
              </select>
            </div>
          </div>

          {/* Name */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              Full Name <span style={{ color: '#C84B0F' }}>*</span>
            </label>
            <input value={form.full_name} onChange={e => set('full_name', e.target.value)}
              placeholder="e.g. Priya Sharma"
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #F0DCC8', borderRadius: 9, fontSize: 13, color: '#1C0A00', background: '#FFFCFA', outline: 'none', boxSizing: 'border-box' }} />
          </div>

          {/* Email */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              Email <span style={{ color: '#C84B0F' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <Mail size={14} color="#C84B0F" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                placeholder="school@example.com"
                style={{ width: '100%', padding: '9px 12px 9px 32px', border: '1px solid #F0DCC8', borderRadius: 9, fontSize: 13, color: '#1C0A00', background: '#FFFCFA', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>

          {/* Mobile */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Mobile</label>
            <div style={{ position: 'relative' }}>
              <Phone size={14} color="#C84B0F" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input value={form.mobile} onChange={e => set('mobile', e.target.value)}
                placeholder="9876543210"
                style={{ width: '100%', padding: '9px 12px 9px 32px', border: '1px solid #F0DCC8', borderRadius: 9, fontSize: 13, color: '#1C0A00', background: '#FFFCFA', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>

          {/* Password */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              Password <span style={{ color: '#C84B0F' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <input type={showPwd ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)}
                placeholder="Min 6 characters"
                style={{ width: '100%', padding: '9px 36px 9px 12px', border: '1px solid #F0DCC8', borderRadius: 9, fontSize: 13, color: '#1C0A00', background: '#FFFCFA', outline: 'none', boxSizing: 'border-box' }} />
              <button onClick={() => setShowPwd(v => !v)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex', padding: 0 }}>
                {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #F3EDE8', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '9px 18px', border: '1px solid #F0DCC8', borderRadius: 9, fontSize: 13, fontWeight: 600, color: '#6B4422', background: '#fff', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding: '9px 22px', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 700, color: '#fff', background: saving ? '#E8A882' : '#C84B0F', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            {saving ? <>
              <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              Creating…
            </> : '+ Create User'}
          </button>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────
export default function SchoolUsersPage() {
  const [users, setUsers]       = useState([]);
  const [schools, setSchools]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filterSchool, setFilterSchool] = useState('');
  const [showCreate, setShowCreate]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search, schoolId: filterSchool });
      const [usersRes, schoolsRes] = await Promise.all([
        api.get(`/school-users?${params}`),
        api.get('/school-users/schools-dropdown'),
      ]);
      setUsers(usersRes.data.data || []);
      setSchools(schoolsRes.data.data || []);
    } catch (e) {
      toast.error('Failed to load');
    } finally { setLoading(false); }
  }, [search, filterSchool]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (id, isActive) => {
    try {
      await api.patch(`/school-users/${id}/toggle`);
      toast.success(isActive ? 'User deactivated' : 'User activated');
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Users size={22} color="#C84B0F" />
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1C0A00', margin: 0 }}>School Users</h1>
          </div>
          <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>Manage login accounts for the School Portal</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: '#C84B0F', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          <Plus size={15} /> Create School User
        </button>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', border: '1px solid #F0DCC8', borderRadius: 14, padding: '14px 18px', marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 8, background: '#FFF8F4', border: '1px solid #F0DCC8', borderRadius: 9, padding: '8px 12px' }}>
          <Search size={14} color="#C84B0F" />
          <input placeholder="Search by name, email or mobile…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, color: '#1C0A00', flex: 1 }} />
          {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex', padding: 0 }}><X size={13} /></button>}
        </div>
        <div style={{ position: 'relative', minWidth: 200 }}>
          <School size={14} color="#C84B0F" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          <select value={filterSchool} onChange={e => setFilterSchool(e.target.value)}
            style={{ width: '100%', padding: '9px 12px 9px 30px', border: '1px solid #F0DCC8', borderRadius: 9, fontSize: 13, color: '#1C0A00', background: '#FFFCFA', appearance: 'none', outline: 'none', cursor: 'pointer' }}>
            <option value="">All schools</option>
            {schools.map(s => <option key={s.id} value={s.id}>{s.school_name}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #F0DCC8', borderRadius: 14, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#FFF8F4' }}>
              {['User', 'School', 'Mobile', 'Status', 'Last Login', 'Created', 'Action'].map(h => (
                <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, color: '#9CA3AF', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #F0DCC8', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: '48px 0', textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#9CA3AF', fontSize: 13 }}>
                  <div style={{ width: 18, height: 18, border: '2px solid #F0DCC8', borderTopColor: '#C84B0F', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  Loading…
                </div>
              </td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: '48px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>No school users found</td></tr>
            ) : users.map((u, i) => (
              <tr key={u.id} style={{ borderTop: '1px solid #FDF0E8', background: i % 2 === 0 ? '#fff' : '#FFFCFA' }}>
                <td style={{ padding: '11px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#FEF3EE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#C84B0F', flexShrink: 0 }}>
                      {u.full_name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: '#1C0A00' }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF' }}>{u.email}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '11px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: '#1D4ED8', fontWeight: 500 }}>
                    <School size={13} /> {u.school_name}
                  </div>
                </td>
                <td style={{ padding: '11px 16px', color: '#6B4422' }}>{u.mobile || '—'}</td>
                <td style={{ padding: '11px 16px' }}>
                  {u.is_active
                    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: '#F0FDF4', color: '#15803D' }}><CheckCircle size={11} /> Active</span>
                    : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: '#FEF2F2', color: '#B91C1C' }}><XCircle size={11} /> Inactive</span>
                  }
                </td>
                <td style={{ padding: '11px 16px', fontSize: 12, color: '#9CA3AF' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={11} />{fmt(u.last_login)}</div>
                </td>
                <td style={{ padding: '11px 16px', fontSize: 12, color: '#9CA3AF' }}>{fmt(u.created_at)}</td>
                <td style={{ padding: '11px 16px' }}>
                  <button onClick={() => handleToggle(u.id, u.is_active)}
                    style={{ padding: '5px 12px', border: `1px solid ${u.is_active ? '#FECACA' : '#BBF7D0'}`, borderRadius: 7, fontSize: 11, fontWeight: 600, background: u.is_active ? '#FEF2F2' : '#F0FDF4', color: u.is_active ? '#B91C1C' : '#15803D', cursor: 'pointer' }}>
                    {u.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateModal schools={schools} onClose={() => setShowCreate(false)} onCreated={load} />}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
